import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import MenuBar from "../MenuBar";
import { apiFetch } from "../api";
import "./TomographieStacks.css";
import { PLIS_RECONNUS } from "../utils/empilementPopulations";

const PAGE_SIZES = [8, 12, 16, 24];
const PLOTLY_CDN = "https://cdn.plot.ly/plotly-2.35.2.min.js";

let plotlyLoader = null;
function loadPlotly() {
  if (window.Plotly) return Promise.resolve(window.Plotly);
  if (plotlyLoader) return plotlyLoader;
  plotlyLoader = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = PLOTLY_CDN;
    s.async = true;
    s.onload = () => resolve(window.Plotly);
    s.onerror = () => reject(new Error("Impossible de charger Plotly"));
    document.head.appendChild(s);
  });
  return plotlyLoader;
}

function colorYlOrRd(t) {
  const x = Math.max(0, Math.min(1, t));
  const stops = [
    [0.0, [255, 255, 204]],
    [0.25, [254, 217, 118]],
    [0.5, [253, 141, 60]],
    [0.75, [227, 26, 28]],
    [1.0, [128, 0, 38]],
  ];
  let i = 0;
  while (i < stops.length - 1 && x > stops[i + 1][0]) i += 1;
  const [t0, c0] = stops[i];
  const [t1, c1] = stops[Math.min(i + 1, stops.length - 1)];
  const u = t1 === t0 ? 0 : (x - t0) / (t1 - t0);
  const rgb = c0.map((v, k) => Math.round(v + (c1[k] - v) * u));
  return `rgb(${rgb[0]},${rgb[1]},${rgb[2]})`;
}

/** Heatmap Plotly comme Site_Affichage.py (bande continue, zsmooth). */
function HeatmapStrip({ values, labels, zmax, unitLabel, formatKind }) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    let cancelled = false;

    const unitFmt =
      formatKind === "gm"
        ? (v) => `${Number(v).toFixed(1)} pts`
        : (v) => Number(v).toFixed(3);

    if (!values?.length) {
      el.innerHTML =
        '<div class="tomo-hm-empty">Pas de transition (1 seul pli)</div>';
      return undefined;
    }

    (async () => {
      try {
        const Plotly = await loadPlotly();
        if (cancelled || !ref.current) return;

        const n = values.length;
        const idxArr = values.map((_, i) => i);
        const z = values.map((v) => [v]);
        const text = values.map((v, i) => [
          `${labels[i]} — ${v == null ? "pas de référence" : unitFmt(v)}`,
        ]);

        const trace = {
          z,
          x: [unitLabel],
          y: idxArr,
          type: "heatmap",
          colorscale: "YlOrRd",
          reversescale: true,
          zmin: 0,
          zmax,
          showscale: false,
          zsmooth: "best",
          xgap: 0,
          ygap: 0,
          text,
          hovertemplate: "%{text}<extra></extra>",
        };

        const layout = {
          margin: { l: 46, r: 4, t: 2, b: 18 },
          height: Math.max(70, n * 15 + 22),
          paper_bgcolor: "rgba(0,0,0,0)",
          plot_bgcolor: "rgba(255,255,255,0.04)",
          font: { color: "#9a9a92" },
          xaxis: {
            tickfont: { size: 9, color: "#9a9a92" },
            showgrid: false,
            zeroline: false,
            fixedrange: true,
          },
          yaxis: {
            tickmode: "array",
            tickvals: idxArr,
            ticktext: labels,
            autorange: "reversed",
            tickfont: { size: 8, color: "#9a9a92" },
            showgrid: false,
            zeroline: false,
            fixedrange: true,
          },
        };

        await Plotly.newPlot(ref.current, [trace], layout, {
          displayModeBar: false,
          responsive: true,
        });
      } catch {
        if (!cancelled && ref.current) {
          ref.current.innerHTML =
            '<div class="tomo-hm-empty">Heatmap indisponible</div>';
        }
      }
    })();

    return () => {
      cancelled = true;
      if (el && window.Plotly) {
        try {
          window.Plotly.purge(el);
        } catch {
          /* ignore */
        }
      }
    };
  }, [values, labels, zmax, unitLabel, formatKind]);

  return <div ref={ref} className="tomo-phm" />;
}

function Badge({ conf }) {
  if (conf === "CONFORME") return <span className="tomo-badge ok">Conforme</span>;
  if (conf === "NON CONFORME") return <span className="tomo-badge ko">Non conforme</span>;
  return <span className="tomo-badge un">N/R</span>;
}

function ScaleLegend({ gmMax, densMax }) {
  const stops = [0, 0.25, 0.5, 0.75, 1];
  return (
    <div className="tomo-scale">
      <div className="tomo-scale-bar">
        {stops.map((t) => (
          <span key={t} style={{ background: colorYlOrRd(t) }} />
        ))}
      </div>
      <div className="tomo-scale-meta">
        <span>Faible</span>
        <span>Fort écart</span>
      </div>
      <div className="tomo-scale-vals">
        GM 0–{gmMax} pts · Densité 0–{densMax}
      </div>
    </div>
  );
}

function Pagination({ page, pageCount, onPage, total, from, to }) {
  if (pageCount <= 0) return null;
  const windowPages = [];
  const start = Math.max(1, page - 2);
  const end = Math.min(pageCount, page + 2);
  for (let p = start; p <= end; p += 1) windowPages.push(p);

  return (
    <div className="tomo-pager">
      <div className="tomo-pager-info">
        {total === 0 ? "Aucun résultat" : `${from}–${to} sur ${total}`}
      </div>
      <div className="tomo-pager-btns">
        <button type="button" disabled={page <= 1} onClick={() => onPage(1)} title="Première page">
          «
        </button>
        <button type="button" disabled={page <= 1} onClick={() => onPage(page - 1)} title="Précédent">
          ‹
        </button>
        {start > 1 && <span className="tomo-pager-ellipsis">…</span>}
        {windowPages.map((p) => (
          <button
            key={p}
            type="button"
            className={p === page ? "on" : ""}
            onClick={() => onPage(p)}
          >
            {p}
          </button>
        ))}
        {end < pageCount && <span className="tomo-pager-ellipsis">…</span>}
        <button type="button" disabled={page >= pageCount} onClick={() => onPage(page + 1)} title="Suivant">
          ›
        </button>
        <button type="button" disabled={page >= pageCount} onClick={() => onPage(pageCount)} title="Dernière page">
          »
        </button>
      </div>
    </div>
  );
}

export default function TomographieStacks() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [records, setRecords] = useState([]);
  const [cfg, setCfg] = useState({
    ecartGmMax: 30,
    ecartDensMax: 0.04,
  });
  const [generatedAt, setGeneratedAt] = useState("");
  const [pop, setPop] = useState("all");
  const [stack, setStack] = useState("all");
  const [pageSize, setPageSize] = useState(12);
  const [page, setPage] = useState(1);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const res = await apiFetch("/api/qualite/tomographie");
        const data = await res.json();
        if (!res.ok || data.status !== "success") {
          throw new Error(data.message || `Erreur HTTP ${res.status}`);
        }
        if (cancelled) return;
        setRecords(data.records || []);
        if (data.cfg) setCfg((c) => ({ ...c, ...data.cfg }));
        setGeneratedAt(data.generatedAt || "");
      } catch (e) {
        if (!cancelled) setError(e.message || "Erreur de chargement");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const stackOptions = useMemo(() => {
    return [...records].sort((a, b) => {
      const na = a.idNorm && /^\d+$/.test(a.idNorm) ? Number(a.idNorm) : 0;
      const nb = b.idNorm && /^\d+$/.test(b.idNorm) ? Number(b.idNorm) : 0;
      return na - nb;
    });
  }, [records]);

  const filtered = useMemo(() => {
    if (stack !== "all") return records.filter((r) => r.idNorm === stack);
    return records.filter((r) => pop === "all" || r.popBucket === pop);
  }, [records, pop, stack]);

  const stats = useMemo(() => {
    let ok = 0;
    let ko = 0;
    let nr = 0;
    for (const r of filtered) {
      if (r.conf === "CONFORME") ok += 1;
      else if (r.conf === "NON CONFORME") ko += 1;
      else nr += 1;
    }
    return { ok, ko, nr, total: filtered.length };
  }, [filtered]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize) || 1);
  const safePage = Math.min(page, pageCount);

  const pageItems = useMemo(() => {
    const start = (safePage - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, safePage, pageSize]);

  const from = filtered.length === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const to = Math.min(safePage * pageSize, filtered.length);

  function resetPage() {
    setPage(1);
  }

  return (
    <div className="tomo-page">
      <MenuBar active="qualite" />
      <div className="tomo-wrap">
        <header className="tomo-hero">
          <div className="tomo-hero-text">
            <p className="tomo-kicker">Qualité · Contrôle stack qualité</p>
            <h1 className="tomo-title">Carte de chaleur des stacks suivant les écarts des Gains Massiques et des Densités </h1>
            <p className="tomo-sub">
              Écarts successifs entre plis (gain massique et densité anhydre)
              {generatedAt ? ` · mis à jour ${generatedAt}` : ""}
            </p>
          </div>
          <button type="button" className="tomo-back" onClick={() => navigate(-1)}>
            Retour
          </button>
        </header>

        <div className="tomo-stats">
          <div className="tomo-stat">
            <span className="tomo-stat-val">{stats.total}</span>
            <span className="tomo-stat-lab">Stacks filtrés</span>
          </div>
          <div className="tomo-stat ok">
            <span className="tomo-stat-val">{stats.ok}</span>
            <span className="tomo-stat-lab">Conformes</span>
          </div>
          <div className="tomo-stat ko">
            <span className="tomo-stat-val">{stats.ko}</span>
            <span className="tomo-stat-lab">Non conformes</span>
          </div>
          <div className="tomo-stat muted">
            <span className="tomo-stat-val">{records.length}</span>
            <span className="tomo-stat-lab">Chargés</span>
          </div>
          <ScaleLegend gmMax={cfg.ecartGmMax} densMax={cfg.ecartDensMax} />
        </div>

        <div className="tomo-bar">
          <div className="tomo-grp">
            <span className="tomo-lbl">Épaisseur</span>
            {[
              ["all", "Toutes"],
              ...PLIS_RECONNUS.map((n) => [String(n), `${n} plis`]),
              ["autre", "Autre"],
            ].map(([val, lab]) => (
              <button
                key={val}
                type="button"
                className={`tomo-f ${pop === val ? "on" : ""}`}
                onClick={() => {
                  setPop(val);
                  setStack("all");
                  resetPage();
                }}
              >
                {lab}
              </button>
            ))}
          </div>

          <div className="tomo-grp">
            <span className="tomo-lbl">Stack</span>
            <select
              className="tomo-sel tomo-selWide"
              value={stack}
              onChange={(e) => {
                setStack(e.target.value);
                resetPage();
              }}
            >
              <option value="all">Tous les stacks</option>
              {stackOptions.map((r) => (
                <option key={r.idNorm || r.id} value={r.idNorm}>
                  {r.id} — {r.date}
                </option>
              ))}
            </select>
          </div>

          <div className="tomo-grp tomo-grp-end">
            <span className="tomo-lbl">Par page</span>
            <select
              className="tomo-sel"
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                resetPage();
              }}
            >
              {PAGE_SIZES.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>
        </div>

        {loading && (
          <div className="tomo-empty">
            <div className="tomo-spinner" />
            Chargement des stacks…
          </div>
        )}
        {error && !loading && <div className="tomo-empty tomo-err">{error}</div>}

        {!loading && !error && (
          <>
            <div className="tomo-grid">
              {pageItems.length === 0 ? (
                <div className="tomo-empty">Aucun stack à afficher pour ces filtres.</div>
              ) : (
                pageItems.map((rec, idx) => (
                  <article
                    key={rec.idNorm || rec.id}
                    className="tomo-card"
                    style={{ animationDelay: `${Math.min(idx, 8) * 40}ms` }}
                  >
                    <div className="tomo-hd">
                      <span className="tomo-cid">{rec.id}</span>
                      <Badge conf={rec.conf} />
                    </div>
                    <div className="tomo-meta">
                      <span>{rec.date}</span>
                      <span className="tomo-pill">{rec.popLabel}</span>
                    </div>
                    <div className="tomo-gbox">
                      <div className="tomo-glab">|Écart GM| · |pli i − i−1|</div>
                      <HeatmapStrip
                        values={rec.ecartGm}
                        labels={rec.transLabels}
                        zmax={cfg.ecartGmMax}
                        unitLabel="|ΔGM|"
                        formatKind="gm"
                      />
                    </div>
                    <div className="tomo-gbox">
                      <div className="tomo-glab">|Écart densité| · |pli i − i−1|</div>
                      <HeatmapStrip
                        values={rec.ecartDens}
                        labels={rec.transLabels}
                        zmax={cfg.ecartDensMax}
                        unitLabel="|ΔDens|"
                        formatKind="dens"
                      />
                    </div>
                  </article>
                ))
              )}
            </div>

            <Pagination
              page={safePage}
              pageCount={pageCount}
              onPage={setPage}
              total={filtered.length}
              from={from}
              to={to}
            />
          </>
        )}
      </div>
    </div>
  );
}
